import { useState } from "react";
import { paths } from "../constants";
import { useColors } from "../ColorsContext";
import Pill from "../components/Pill";

export default function ScreenThemeSelect({ onSelect, onQuiz, onBack, recommended, layout }) {
  const C = useColors();
  const [selected, setSelected] = useState(
    recommended ? paths.find(p => p.id === recommended) || null : null
  );
  const [hovered, setHovered] = useState(null);

  const { scale = 1, offsetX = 0, vpWidth = 390 } = layout || {};
  const extraLeft = offsetX / scale;
  const fullWidth = vpWidth / scale;
  const edgeStyle = {
    marginLeft: -extraLeft,
    width: fullWidth,
    paddingLeft: extraLeft + 14,
    paddingRight: extraLeft + 14,
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", background: C.navy }}>

      {/* Sticky top — back button + quiz strip */}
      <div style={{
        background: C.navyMid, paddingTop: 10, paddingBottom: 10,
        borderBottom: `1px solid ${C.dim}`, flexShrink: 0,
        ...edgeStyle,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Back button */}
          <button
            onClick={onBack}
            style={{
              background: "transparent", border: "none",
              color: C.muted, fontSize: 20, cursor: "pointer",
              padding: "4px 6px", lineHeight: 1, flexShrink: 0,
            }}
          >←</button>

          {/* Quiz strip or completion badge */}
          {recommended ? (
            <div style={{
              flex: 1, background: `${C.green}15`, borderRadius: 8,
              padding: "8px 12px", border: `1px solid ${C.green}44`,
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <div style={{ fontSize: 15 }}>✅</div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.green }}>Quiz complete</div>
                <div style={{ fontSize: 10, color: C.muted }}>Recommended path highlighted below</div>
              </div>
            </div>
          ) : (
            <div style={{
              flex: 1, background: `${C.pink}12`, borderRadius: 8,
              padding: "8px 12px", border: `1px solid ${C.pink}44`,
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.white }}>Not sure which path fits you?</div>
                <div style={{ fontSize: 10, color: C.muted }}>3 quick questions</div>
              </div>
              <button
                onClick={onQuiz}
                style={{
                  background: C.pink, color: "#fff", border: "none",
                  borderRadius: 20, padding: "8px 18px",
                  fontSize: 17, fontWeight: 800, cursor: "pointer",
                  flexShrink: 0, boxShadow: `0 0 12px ${C.pink}60`,
                }}
              >TAKE QUIZ →</button>
            </div>
          )}
        </div>
      </div>

      {/* Scrollable path cards */}
      <div style={{ flex: 1, overflow: "auto", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
        {paths.map(p => {
          const isSelected = selected?.id === p.id;
          const isHovered = hovered === p.id;
          const isRecommended = recommended === p.id;
          return (
            <div
              key={p.id}
              onClick={() => setSelected(p)}
              onMouseEnter={() => setHovered(p.id)}
              onMouseLeave={() => setHovered(null)}
              style={{
                borderRadius: 10, padding: "11px 14px",
                background: isSelected ? `${p.color}18` : C.card,
                border: `2px solid ${isSelected ? p.color : isHovered ? C.dim : "transparent"}`,
                cursor: "pointer", transition: "all 0.2s",
                position: "relative",
              }}
            >
              {isRecommended && (
                <div style={{
                  position: "absolute", top: -8, right: 12,
                  background: C.gold, color: C.navy,
                  fontSize: 9, fontWeight: 800, padding: "2px 8px",
                  borderRadius: 10, letterSpacing: 0.5,
                }}>★ RECOMMENDED</div>
              )}

              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <div style={{
                  width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                  border: `2px solid ${isSelected ? p.color : C.dim}`,
                  background: isSelected ? p.color : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.2s",
                }}>
                  {isSelected && <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#fff" }} />}
                </div>
                <div style={{
                  width: 38, height: 38, borderRadius: 8, flexShrink: 0,
                  background: `${p.color}22`, display: "flex",
                  alignItems: "center", justifyContent: "center", fontSize: 20,
                  border: `1px solid ${p.color}44`,
                }}>{p.icon}</div>
                <div style={{ fontWeight: 700, color: isSelected ? p.color : C.white, fontSize: 14 }}>
                  {p.label}
                </div>
              </div>

              <div style={{ fontSize: 11, color: C.light, marginBottom: 3, lineHeight: 1.4, paddingLeft: 30 }}>
                {p.desc}
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, paddingLeft: 30 }}>
                {p.tags.map(t => <Pill key={t} label={t} color={p.color} />)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Confirm CTA */}
      <div style={{
        paddingTop: 12, paddingBottom: 12,
        background: C.navyMid, borderTop: `1px solid ${C.dim}`, flexShrink: 0,
        ...edgeStyle,
      }}>
        {selected && (
          <div style={{ fontSize: 11, color: selected.color, marginBottom: 8, textAlign: "center" }}>
            ✓ {selected.label} selected · {selected.duration}
          </div>
        )}
        <button
          onClick={() => selected && onSelect(selected)}
          disabled={!selected}
          style={{
            width: "100%", padding: "14px 0",
            background: selected ? C.pink : C.dim,
            color: selected ? "#fff" : C.muted,
            border: "none", borderRadius: 8, fontWeight: 700,
            fontSize: 17, cursor: selected ? "pointer" : "not-allowed",
            letterSpacing: 0.5, transition: "all 0.2s", minHeight: 48,
          }}
        >
          CONFIRM & CONTINUE →
        </button>
      </div>
    </div>
  );
}
